import React, { useEffect, useRef, useState, useMemo } from 'react';
import { string, object, array, bool, func, oneOfType, element, number, node, arrayOf, objectOf, shape, } from 'prop-types';
import Button from '../button';
import useDragHelper from '../../hooks/use-drag-helper';
import { useCancel, CancelError } from '../../hooks/use-cancel';
import { bytesToSize, getFileEnding, getIsBelowMaxSize, loadGivenFile, validateFileEnding, } from './logic';
import Progress from './components/progress';
import setClass from '../../common/set-class';
import { listArrayItems } from '../../common/list-array-items';
import Icon from '../icon';
const resolveNonEmptyStringValue = (subject) => typeof subject === 'string' && Boolean(subject) ? subject : false;
const states = {
    errorSelecting: 'errorSelecting',
    errorUploading: 'errorUploading',
    errorValidating: 'errorValidating',
    idle: 'idle',
    fileSelected: 'fileSelected',
    uploading: 'uploading',
    validating: 'validating',
    uploaded: 'uploaded',
};
const defaultCurrentFile = {
    fileUploadId: '',
    file: null,
    fileSHA: '',
    progress: 0,
    state: states.idle,
};
const hasErrorTypes = {
    validationError: 'validationError',
    uploadError: 'uploadError',
    fileSelectError: 'fileSelectError',
    withError: 'withError',
};
const defaultHasError = {
    validationError: { message: '', type: hasErrorTypes.validationError },
    uploadError: { message: '', type: hasErrorTypes.uploadError },
    fileSelectError: { message: '', type: hasErrorTypes.fileSelectError },
};
export const defaultRenderInfo = ({ isDisabled, currentFile, isMultiPreview, }) => {
    const displayFileName = (fileName = '') => {
        const MULTI_PREVIEW_MAX_CHARS_FOR_TRUNCATE = 30;
        const FROM_BEGINNING_PARTIAL_FILE_NAME_END_POS = 20;
        const FROM_ENDING_PARTIAL_FILE_NAME_START_POS = 10;
        const hasFileExt = fileName.includes('.');
        if (isMultiPreview &&
            hasFileExt &&
            fileName.length >= MULTI_PREVIEW_MAX_CHARS_FOR_TRUNCATE) {
            const splitFileName = fileName.split('.');
            const fileExtension = splitFileName[splitFileName.length - 1];
            const [fileNameNoExt] = fileName.split(`.${fileExtension}`);
            return `${fileNameNoExt.substring(0, FROM_BEGINNING_PARTIAL_FILE_NAME_END_POS)}...${fileNameNoExt.substring(fileNameNoExt.length - FROM_ENDING_PARTIAL_FILE_NAME_START_POS, fileNameNoExt.length)}.${fileExtension}`;
        }
        return fileName;
    };
    return (React.createElement("div", { className: "b-flex-col" },
        React.createElement("span", { className: setClass('b-txt-bold', isDisabled && 'b-txt-light') }, displayFileName(currentFile.file.name)),
        React.createElement("span", { className: "b-txt-light" }, bytesToSize(currentFile.file.size)),
        currentFile.fileSHA && (React.createElement("span", { className: "b-txt-light" },
            "SHA-256: ",
            currentFile.fileSHA))));
};
const multiUploadPreviewWidth = '360px';
const Upload = ({ className, style, isMulti, isDisabled, allowedTypes, onFileSelect, onUpload, onValidate, onUploaded, onDelete, onCancel, onCancelError, onError, fileIcons, icon, withError, forceWithFile, maxFileSize, uploadInstructions, renderInfo = defaultRenderInfo, }) => {
    const inputRef = useRef();
    const [currentFile, setCurrentFileOriginal] = useState(defaultCurrentFile);
    const [multiFiles, setMultiFiles] = useState([]);
    const [hasError, setHasErrorOriginal] = useState(defaultHasError);
    const isIdle = currentFile.state === states.idle;
    const isFileSelected = currentFile.state === states.fileSelected;
    const isUploading = currentFile.state === states.uploading;
    const isValidating = currentFile.state === states.validating;
    const isUploaded = currentFile.state === states.uploaded;
    const isErrorSelecting = currentFile.state === states.errorSelecting;
    const isFileExists = Boolean(currentFile.file);
    const isDuringUploadProcess = isUploading || isValidating;
    const hasFileSelectError = Boolean(hasError.fileSelectError.message);
    const shouldShowFileSelectView = isIdle || isErrorSelecting || !isFileExists;
    const withErrorMessage = resolveNonEmptyStringValue(withError);
    const activeError = Object.values(hasError).find(({ message }) => Boolean(message)) ?? {
        message: '',
        type: '',
    };
    const activeErrorMessage = withErrorMessage || activeError.message;
    const anyErrors = Boolean(activeErrorMessage);
    const { cancel: cancelUpload, getCurrentIsCancelled: getUploadCurrentlyCancelled, createCancellableUpdate, } = useCancel({
        // @ts-expect-error ts-migrate(2322) FIXME: Type '() => any' is not assignable to type 'undefi... Remove this comment to see the full error message
        resolveCancelEffect: () => isDisabled && isDuringUploadProcess,
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'any' is not assignable to type 'never'.
        cancelEffectDeps: [isDisabled, isDuringUploadProcess],
        // @ts-expect-error ts-migrate(2322) FIXME: Type '() => boolean' is not assignable to type 'un... Remove this comment to see the full error message
        resolveResetCancelEffect: () => isFileSelected,
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'boolean' is not assignable to type 'never'.
        resetCancelEffectDeps: [isFileSelected],
    });
    const [setCurrentFile, setHasError] = useMemo(() => {
        const options = {
            cancelErrorMessage: 'Upload cancelled',
        };
        return [
            createCancellableUpdate(setCurrentFileOriginal, options),
            createCancellableUpdate(setHasErrorOriginal, options),
        ];
    }, [createCancellableUpdate]);
    const setAllFiles = (file, fileMeta) => {
        const { file: f } = file;
        const isCurrentFileAllowed = validateFileEnding(f, allowedTypes);
        if (isCurrentFileAllowed) {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '(prev: never[]) => any[]' is not... Remove this comment to see the full error message
            setMultiFiles((prev) => [...prev, file]);
        }
        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
        setCurrentFile((prev) => ({
            ...prev,
            ...fileMeta,
        }));
    };
    const onDeleteAsync = onDelete
        ? createCancellableUpdate(onDelete, {
            isLazy: true,
            cancelErrorMessage: 'Upload deleted',
        })
        : null;
    const reset = useMemo(() => {
        const handleResetRef = () => {
            if (inputRef && inputRef.current) {
                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                inputRef.current.value = null;
            }
        };
        const getError = (errorType, allowedFileTypes, isCurrentFileAllowed, maxFileSizeBytes, isBelowMaxSize, formatBytes = bytesToSize) => {
            if (!errorType) {
                return {
                    message: '',
                    type: null,
                };
            }
            const isOverMaxSizeError = isBelowMaxSize !== undefined && isBelowMaxSize === false;
            const errorMessageMap = {
                validationError: 'Something went wrong while validating the file, please try again.',
                uploadError: 'Something went wrong while uploading the file, please try again.',
                fileSelectError: () => {
                    if (allowedFileTypes && !isCurrentFileAllowed) {
                        return `This doesn't appear to be a valid file type. Allowed extensions: ${allowedFileTypes.join(', ')}`;
                    }
                    if (isOverMaxSizeError) {
                        return `File size must be less than ${formatBytes(maxFileSizeBytes)}`;
                    }
                    return '';
                },
            };
            const errorMessage = 
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            errorMessageMap[errorType] ?? 'Something went wrong, please try again.';
            return typeof errorMessage === 'function'
                ? { type: errorType, message: errorMessage() }
                : { type: errorType, message: errorMessage };
        };
        const resetHelpers = ({ errorType, allowedTypes: allowedFileTypes, isCurrentFileAllowed, maxFileSize: maxFileSizeBytes, isBelowMaxSize, } = {}) => ({
            idleCurrentFile: { progress: 0, state: states.idle },
            resetRef: handleResetRef,
            error: getError(errorType, allowedFileTypes, isCurrentFileAllowed, maxFileSizeBytes, isBelowMaxSize),
        });
        const createReset = ({ updateCurrentFile, updateHasError }) => (resetArgs) => {
            if (typeof updateCurrentFile === 'function') {
                // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                setCurrentFile((prev) => updateCurrentFile(resetHelpers(resetArgs), prev));
            }
            if (typeof updateHasError === 'function') {
                const helpers = resetHelpers(resetArgs);
                // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                setHasError((prev) => updateHasError(helpers, prev));
            }
        };
        return {
            toAllDefault: createReset({
                updateCurrentFile: ({ resetRef }) => {
                    resetRef();
                    return {
                        ...defaultCurrentFile,
                    };
                },
                updateHasError: () => ({
                    ...defaultHasError,
                }),
            }),
            toDefaultCurrentFile: createReset({
                updateCurrentFile: () => ({
                    ...defaultCurrentFile,
                }),
            }),
            toDefaultHasError: createReset({
                updateHasError: () => ({
                    ...defaultHasError,
                }),
            }),
            toValidationError: createReset({
                // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'prevCurrFile' implicitly has an 'any' t... Remove this comment to see the full error message
                updateCurrentFile: ({ idleCurrentFile }, prevCurrFile) => ({
                    ...prevCurrFile,
                    ...idleCurrentFile,
                    state: states.errorValidating,
                }),
                updateHasError: ({ error }) => ({
                    ...defaultHasError,
                    validationError: error,
                }),
            }),
            toUploadError: createReset({
                // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'prevCurrFile' implicitly has an 'any' t... Remove this comment to see the full error message
                updateCurrentFile: ({ idleCurrentFile }, prevCurrFile) => ({
                    ...prevCurrFile,
                    ...idleCurrentFile,
                    state: states.errorUploading,
                }),
                updateHasError: ({ error }) => ({
                    ...defaultHasError,
                    uploadError: error,
                }),
            }),
            toFileSelectError: createReset({
                updateHasError: ({ error }) => ({
                    ...defaultHasError,
                    fileSelectError: error,
                }),
            }),
        };
    }, [setCurrentFile, setHasError]);
    const [ref, isHovering] = useDragHelper(isDisabled, (e) => {
        if (isUploading || isValidating) {
            return;
        }
        const file = e.dataTransfer.files[0];
        if (!file) {
            return;
        }
        const isCurrentFileAllowed = validateFileEnding(file, allowedTypes);
        const isBelowMaxSize = getIsBelowMaxSize(maxFileSize, file.size);
        const selectDroppedFile = () => {
            // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
            setCurrentFile({
                ...defaultCurrentFile,
                file,
                state: states.fileSelected,
            });
            if (onFileSelect) {
                onFileSelect(file);
            }
        };
        const beginDroppedFileUpload = async ({ isReUpload }) => {
            if (isReUpload && onDeleteAsync) {
                // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                const [{ awaitable: tryClearPrevFile }] = onDeleteAsync(currentFile, {
                    error: null,
                });
                const [error] = await tryClearPrevFile();
                if (error === null) {
                    return selectDroppedFile();
                }
            }
            return selectDroppedFile();
        };
        if (isCurrentFileAllowed && isBelowMaxSize) {
            beginDroppedFileUpload({ isReUpload: isFileExists });
        }
        else {
            reset.toFileSelectError({
                errorType: hasErrorTypes.fileSelectError,
                allowedTypes,
                isCurrentFileAllowed,
                maxFileSize,
                isBelowMaxSize,
            });
        }
    }, 
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'any' is not assignable to type 'never'.
    { deps: [allowedTypes, isDuringUploadProcess, isFileExists] });
    const openFileSelector = () => {
        if (inputRef?.current) {
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            inputRef.current.click();
        }
    };
    useEffect(() => {
        if (forceWithFile) {
            loadGivenFile(forceWithFile, allowedTypes, isMulti)
                .then((r) => {
                if (isMulti && !!r.length) {
                    setMultiFiles(r);
                    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
                    reset.toAllDefault();
                }
                else if (r.file) {
                    // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                    setCurrentFile({
                        ...defaultCurrentFile,
                        file: r.file,
                        fileSHA: r.sha256 || '',
                        state: states.uploaded,
                    });
                }
            })
                .then(() => {
                // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
                reset.toDefaultHasError();
            })
                .catch((error) => {
                if (onDelete) {
                    onDelete(currentFile.file, { error });
                }
                // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
                reset.toDefaultCurrentFile();
            });
        }
    }, [forceWithFile]);
    useEffect(() => {
        if (onError) {
            onError(activeError.message, {
                type: activeError.type,
                errorTypes: hasErrorTypes,
            });
        }
    }, [hasError]);
    useEffect(() => {
        const CHECK_FILE_SELECT_ERROR = isFileExists && isUploaded;
        if (CHECK_FILE_SELECT_ERROR) {
            const isCurrentFileAllowed = validateFileEnding(currentFile.file, allowedTypes);
            if (!isCurrentFileAllowed) {
                reset.toFileSelectError({
                    errorType: hasErrorTypes.fileSelectError,
                    allowedTypes,
                    isCurrentFileAllowed,
                    maxFileSize,
                });
            }
        }
    }, [allowedTypes, currentFile, hasFileSelectError, maxFileSize]);
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '() => "exitEffect.before.onValid... Remove this comment to see the full error message
    useEffect(() => {
        let isEffectMounted = true;
        const BEGIN_UPLOAD_STAGE = isFileSelected && onUpload;
        const BEGIN_VALIDATION_STAGE = isValidating && onValidate;
        if (BEGIN_UPLOAD_STAGE) {
            // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
            setCurrentFile((prev) => ({
                ...prev,
                state: states.uploading,
            }));
            // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
            reset.toDefaultHasError();
            onUpload(currentFile.file, (progress) => {
                const isCancelledDuringOnUpload = getUploadCurrentlyCancelled();
                if (isCancelledDuringOnUpload && onCancel) {
                    onCancel(currentFile.file);
                }
                if (isCancelledDuringOnUpload) {
                    return 'exitEffect.during.onUpload.cancel';
                }
                // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                return setCurrentFile((prev) => ({
                    ...prev,
                    progress,
                }));
            })
                .then((data) => {
                const isCancelledAfterOnUpload = getUploadCurrentlyCancelled();
                if (isCancelledAfterOnUpload) {
                    return 'exitEffect.after.onUpload.cancel';
                }
                if (isMulti) {
                    setAllFiles(currentFile, {
                        state: onValidate ? states.validating : states.uploaded,
                    });
                }
                else {
                    // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                    setCurrentFile((prev) => ({
                        ...prev,
                        state: onValidate ? states.validating : states.uploaded,
                    }));
                }
                return Promise.resolve(data);
            })
                .then((data) => {
                if (!onValidate && onUploaded) {
                    return onUploaded(currentFile.file, {
                        type: 'uploadedWithoutValidation',
                        multiFiles,
                        data,
                    });
                }
                return 'exitEffect.after.onUpload.end';
            })
                .catch((uploadError) => {
                const isCancelErrorCaught = uploadError instanceof onCancelError ||
                    uploadError instanceof CancelError ||
                    uploadError.constructor.name === 'Cancel' ||
                    uploadError.name === 'CancelError';
                if (isCancelErrorCaught) {
                    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
                    return reset.toAllDefault();
                }
                return reset.toUploadError({
                    errorType: hasErrorTypes.uploadError,
                });
            });
        }
        if (BEGIN_VALIDATION_STAGE) {
            const isCancelledBeforeOnValidate = getUploadCurrentlyCancelled();
            if (isCancelledBeforeOnValidate) {
                return 'exitEffect.before.onValidate.cancel';
            }
            onValidate(currentFile.file, {
                getUploadCurrentlyCancelled,
                CancelError,
            })
                .then((validatedSHA) => {
                const isCancelledAfterOnValidate = getUploadCurrentlyCancelled();
                if (isCancelledAfterOnValidate) {
                    return 'exitEffect.after.onValidate.cancel';
                }
                if (isEffectMounted) {
                    if (isMulti) {
                        setAllFiles(currentFile, {
                            state: states.uploaded,
                            fileSHA: validatedSHA || '',
                        });
                    }
                    else {
                        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                        setCurrentFile((prev) => ({
                            ...prev,
                            state: states.uploaded,
                            fileSHA: validatedSHA || '',
                        }));
                    }
                    if (onUploaded) {
                        return onUploaded(currentFile.file, validatedSHA || '', multiFiles);
                    }
                }
                return 'exitEffect.after.onValidate.end';
            })
                .catch((validationError) => {
                if (validationError instanceof CancelError) {
                    return 'exitEffect.catch.onValidate.cancel';
                }
                return reset.toValidationError({
                    errorType: hasErrorTypes.validationError,
                });
            });
        }
        return () => {
            isEffectMounted = false;
        };
    }, [currentFile]);
    return (React.createElement("div", { className: `b-flex-col ${className}` },
        isMulti && isDisabled ? null : (React.createElement("div", { 
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'boolean | MutableRefObject<undefined>' is no... Remove this comment to see the full error message
            ref: ref, className: setClass('b-up-container b-up-lg hook-upload-container', isUploading && 'b-up-lg-uploader', isUploaded && 'b-up-lg-uploaded b-up-container__loaded', isHovering && 'b-up-container__focus', isDisabled && 'b-up-container__disabled', !isIdle && !isDisabled && 'b-up-container__selected', anyErrors && 'b-up-container__error'), style: style, onClick: () => isIdle && openFileSelector(), onKeyPress: (e) => {
                if (e.key === 'Enter' && isIdle) {
                    openFileSelector();
                }
            }, role: "button", tabIndex: 0, "aria-dropeffect": "copy" },
            React.createElement("input", { className: "b-icon-up-xs__input", type: "file", accept: allowedTypes.join(','), disabled: isDisabled || currentFile.file, 
                // @ts-expect-error ts-migrate(2322) FIXME: Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
                ref: inputRef, onChange: (e) => {
                    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
                    const file = e.target.files[0];
                    if (!file) {
                        return;
                    }
                    const isCurrentFileAllowed = validateFileEnding(file, allowedTypes);
                    const isBelowMaxSize = getIsBelowMaxSize(maxFileSize, file.size);
                    if (isCurrentFileAllowed && isBelowMaxSize) {
                        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
                        setCurrentFile((prev) => ({
                            ...prev,
                            file,
                            state: states.fileSelected,
                        }));
                        if (onFileSelect) {
                            onFileSelect(file);
                        }
                    }
                    else {
                        reset.toFileSelectError({
                            errorType: hasErrorTypes.fileSelectError,
                            allowedTypes,
                            isCurrentFileAllowed,
                            maxFileSize,
                            isBelowMaxSize,
                        });
                    }
                } }),
            shouldShowFileSelectView ? (React.createElement("div", { className: "b-flex-col b-flex-btw b-flex-vc b-flex-hc b-flex1 hook-upload-instructions" },
                React.createElement(Icon, { name: "file-arrow-up", className: setClass('b-up-icon', isDisabled && 'b-up-icon-disabled') }),
                React.createElement("div", { className: "b-flex-col b-flex-vc" },
                    React.createElement("span", { className: setClass(isDisabled ? 'b-txt-light' : 'b-txt') }, uploadInstructions),
                    React.createElement("span", { className: setClass('b-txt-bold hook-upload-allowed-types', isDisabled && 'b-txt-light') },
                        listArrayItems(allowedTypes),
                        " file")))) : (React.createElement(FileInfoView, { isMulti: isMulti, icon: icon, fileIcons: fileIcons, isDisabled: isDisabled, currentFile: currentFile, renderInfo: renderInfo, isUploading: isUploading, isValidating: isValidating, cancelUpload: cancelUpload, onCancel: onCancel, onDelete: onDelete, onReset: reset.toAllDefault })))),
        anyErrors && (React.createElement("div", { className: "b-up-error" },
            React.createElement(Icon, { name: "triangle-exclamation", className: "b-txt-input__err-icon b-mr-tiny" }),
            React.createElement("p", { className: "b-txt b-txt--error" },
                " ",
                activeErrorMessage,
                " "))),
        isMulti && (React.createElement("div", { className: "b-grid-auto-fill b-mt", style: {
                // @ts-expect-error ts-migrate(2322) FIXME: Type '{ '--b-grid-auto-min-width': string; }' is n... Remove this comment to see the full error message
                '--b-grid-auto-min-width': multiUploadPreviewWidth,
            } }, multiFiles.map((fileObj, idx) => {
            // @ts-expect-error ts-migrate(2339) FIXME: Property 'file' does not exist on type 'never'.
            const hasFile = Object.hasOwnProperty.call(fileObj.file, 'name');
            return hasFile ? (React.createElement("div", { 
                // @ts-expect-error ts-migrate(2339) FIXME: Property 'name' does not exist on type 'never'.
                // eslint-disable-next-line react/no-array-index-key -- might have multiple files with same name for key
                key: `${fileObj.name}-${idx}`, className: setClass('b-up-container__loaded b-pad', isDisabled && 'b-up-container__disabled') },
                React.createElement(FileInfoView, { isMultiPreview: true, fileIdx: idx, icon: icon, fileIcons: fileIcons, isDisabled: isDisabled, currentFile: fileObj, renderInfo: renderInfo, onDelete: onDelete, onReset: () => setMultiFiles((prev) => prev.filter((_f, prevIdx) => prevIdx !== idx)) }))) : null;
        })))));
};
const FileInfoView = ({ icon, fileIcons, isDisabled, currentFile, fileIdx, renderInfo, isMulti, isMultiPreview, isUploading, isValidating, cancelUpload, onCancel, onDelete, onReset, }) => {
    const isSingleUploadOrMultiUploading = !isMulti || (isMulti && isUploading);
    const renderUploadState = () => {
        if (isMultiPreview) {
            return null;
        }
        if (isUploading) {
            return React.createElement(Progress, { progress: currentFile.progress });
        }
        if (isValidating) {
            return (React.createElement("span", { className: "b-txt b-up-validate-text" }, "Validating file..."));
        }
        return null;
    };
    const renderIcon = () => icon ||
        (fileIcons && fileIcons[getFileEnding(currentFile.file)] ? (fileIcons[getFileEnding(currentFile.file)]) : (React.createElement(Icon, { name: "file-arrow-up", className: "b-up-icon" })));
    const renderFileView = () => isSingleUploadOrMultiUploading ? (React.createElement(React.Fragment, null,
        renderIcon(),
        renderInfo({ isDisabled, currentFile, isMultiPreview }))) : null;
    return (React.createElement("div", { className: "b-flex b-flex1 b-flex-g" },
        React.createElement("div", { className: "b-flex-col b-flex1" },
            React.createElement("div", { className: "b-flex b-flex1 b-flex-vc b-flex-g b-ml b-up-type-icon" }, renderFileView()),
            renderUploadState()),
        React.createElement("div", { className: setClass('b-flex', isUploading ? 'b-flex-align-end' : 'b-flex-vc') }, isSingleUploadOrMultiUploading ? (React.createElement(Button, { kind: "link", icon: isUploading ? 'circle-xmark' : 'trash-can', isDisabled: isDisabled, theme: "error", size: "small", onClick: () => {
                if (isDisabled) {
                    return;
                }
                if (cancelUpload) {
                    cancelUpload();
                }
                if (isUploading && onCancel) {
                    onCancel(currentFile.file);
                }
                else if (!isUploading && onDelete) {
                    onDelete(currentFile.file, { error: null, fileIdx });
                    onReset(currentFile.file);
                }
            } })) : null)));
};
FileInfoView.propTypes = {
    icon: element,
    fileIcons: objectOf(element),
    isDisabled: bool,
    currentFile: shape({
        fileUploadId: string,
        file: shape({
            name: string,
            size: number,
            type: string,
            lastModified: number,
        }),
        fileSHA: string,
        progress: number,
        state: string,
    }),
    fileIdx: number,
    isMulti: bool,
    isMultiPreview: bool,
    isUploading: bool,
    isValidating: bool,
    cancelUpload: func,
    onCancel: func,
    onDelete: func,
    onReset: func,
    renderInfo: func,
};
FileInfoView.defaultProps = {
    currentFile: null,
    fileIdx: null,
    isMulti: false,
    isMultiPreview: false,
    isUploading: false,
    isValidating: false,
    isDisabled: false,
    cancelUpload: () => { },
    onReset: () => { },
    onDelete: () => { },
    onCancel: () => { },
    fileIcons: null,
    icon: null,
    renderInfo: defaultRenderInfo,
};
Upload.propTypes = {
    className: string,
    style: object,
    allowedTypes: array,
    isMulti: bool,
    isDisabled: bool,
    onFileSelect: func,
    onUpload: func,
    onValidate: func,
    onUploaded: func,
    onDelete: func,
    onCancel: func,
    onCancelError: func,
    onError: func,
    withError: string,
    fileIcons: object,
    icon: element,
    forceWithFile: oneOfType([
        shape({
            fileUploadId: string,
            file: shape({
                name: string,
                size: number,
                type: string,
                lastModified: number,
            }),
            fileSHA: string,
            progress: number,
            state: string,
        }),
        arrayOf(shape({
            fileUploadId: string,
            file: shape({
                name: string,
                size: number,
                type: string,
                lastModified: number,
            }),
            fileSHA: string,
            progress: number,
            state: string,
        })),
        func,
    ]),
    maxFileSize: number,
    uploadInstructions: oneOfType([string, node]),
    renderInfo: func,
};
Upload.defaultProps = {
    className: '',
    style: {},
    allowedTypes: ['.pkg', '.dmg'],
    isMulti: false,
    isDisabled: false,
    onFileSelect: () => { },
    onUpload: null,
    onValidate: null,
    onUploaded: null,
    onDelete: null,
    onCancel: null,
    onCancelError: CancelError,
    onError: null,
    withError: '',
    fileIcons: null,
    icon: null,
    forceWithFile: null,
    maxFileSize: -1,
    uploadInstructions: React.createElement("span", null, "Drag file here or click to upload"),
    renderInfo: defaultRenderInfo,
};
export default Upload;
